import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { PawPrint, Camera, Calendar } from "lucide-react";
import { useT } from "@/lib/translations";
import { useLanguage } from "@/context/LanguageContext";

export default function GuardianUpdatesPage() {
  const { dogId, token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const L = useT();
  const { locale } = useLanguage();

  useEffect(() => {
    axios.get(`${API}/updates/${dogId}/${token}`)
      .then(res => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [dogId, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <PawPrint className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground">{L("Sayfa bulunamadi veya link gecersiz.")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <nav className="glass-nav sticky top-0 z-50 border-b border-border/50">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <PawPrint className="h-7 w-7 text-primary" strokeWidth={1.5} />
              <span className="font-heading text-xl font-semibold">KAHU</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8" data-testid="guardian-updates-page">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl" data-testid="guardian-updates-title">
            {data.dog_name} - {L("Güncellemeler")}
          </h1>
          <p className="text-muted-foreground mt-2">
            {L("Koruyucu aile olarak {name}'nin son durumunu buradan takip edebilirsiniz.").replace("{name}", data.dog_name)}
          </p>
        </div>

        {data.updates.length === 0 ? (
          <div className="text-center py-16">
            <Camera className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">{L("Henuz guncelleme paylasilmamis.")}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {data.updates.map((update) => (
              <div
                key={update.update_id}
                className="bg-card border border-border/50 rounded-2xl overflow-hidden shadow-sm"
                data-testid={`update-card-${update.update_id}`}
              >
                <img
                  src={update.image_base64}
                  alt={`${data.dog_name} guncelleme`}
                  className="w-full max-h-[500px] object-cover"
                />
                <div className="p-4">
                  {update.note && (
                    <p className="text-sm mb-2">{update.note}</p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(update.created_at).toLocaleDateString(locale === "en" ? "en-US" : "tr-TR", {
                      day: "numeric", month: "long", year: "numeric"
                    })}
                  </div>
                  {update.visibility === "guardian_only" && (
                    <span className="inline-block mt-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      {L("Koruyucu ailelere ozel")}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-10 text-sm text-muted-foreground">
          <p>KAHU - {L("Koruyucu Platform")}</p>
        </div>
      </div>
    </div>
  );
}
